/**
 * คำชมหน้าผลคะแนน (D24, 23 ก.ย. 2569) — ศัพท์วัยรุ่นตามช่วงคะแนน
 *
 * ★ ทำไมเป็น data ไม่ใช่ if-else: คำชมจะถูกเพิ่ม/ตัดบ่อยตามที่เด็กชอบหรือเบื่อ
 *   ถ้ากระจายเป็นเงื่อนไขในหน้าจอ การเพิ่มคำหนึ่งคำจะต้องแก้หลายที่และทดสอบยาก
 *
 * ★ การสุ่มต้อง "นิ่ง" — ผูกกับ `attempt id` ไม่ใช่ `Math.random()` หรือเวลา
 *   ชุดเดิมเปิดกี่ครั้งก็ได้คำเดิมเสมอ (เด็กกดรีเฟรชแล้วคำเปลี่ยนจะงง และ
 *   ภาพเทียบทีละไบต์ของ prod-shots จะพังทุกรอบ)
 *
 * ★ คำที่ **ห้าม** อยู่ใน pool (CK คัดออกแล้ว ไม่เหมาะกับแอปเรียนของเด็ก):
 *   GYATT (เรื่องรูปร่าง) · ZESTY (ใช้ล้อเลียน) · SIMP (ด่าคนอื่น) · RIZZ (เรื่องจีบ) ·
 *   TWEAKING (รากศัพท์จากยาเสพติด) · COOKED เดี่ยว ๆ ในช่วงคะแนนต่ำ
 *   (ใช้ได้เฉพาะรูป "ยังไม่ COOKED" ที่แปลว่ายังแก้ได้) — ตรึงไว้ใน praise.test.ts
 *
 * ★ ช่วง 0-49% ตั้งใจให้ตลกแบบให้กำลังใจ ไม่ใช่ล้อเด็ก — เด็กที่ได้ 8/20 แล้วเจอคำว่า
 *   "จบเห่" จะไม่อยากทำต่อ ทุกคำในช่วงนี้จึงสื่อว่า "ยังไม่จบ เอาใหม่ได้"
 */
export interface Praise {
  /** คำสแลงที่ขึ้นเป็นหัวข้อใหญ่ */
  slang: string;
  /** คำอ่านไทยในวงเล็บ */
  read: string;
  /** ความหมายไทยสั้น ๆ */
  meaning: string;
}

interface Band {
  /** คะแนนต่ำสุดของช่วง (รวมค่านี้) */
  min: number;
  /** ชื่อช่วงไว้อ่านในโค้ดและใน test */
  label: string;
  pool: readonly Praise[];
}

const p = (slang: string, read: string, meaning: string): Praise => ({
  slang,
  read,
  meaning,
});

/** เรียงจากคะแนนสูงไปต่ำ — ตัวเลือกช่วงแรกที่ `pct >= min` คือช่วงที่ใช้ */
export const BANDS: readonly Band[] = [
  {
    min: 100,
    label: "ปังไม่มีที่ติ",
    pool: [
      p("ATE AND LEFT NO CRUMBS", "เอท แอนด์ เลฟต์ โน ครัมส์", "ปังไม่มีที่ติ"),
      p("GOAT", "โกท", "สุดยอดตลอดกาล"),
      p("BIG W", "บิ๊ก ดับเบิลยู", "ชนะครั้งใหญ่"),
      p("PERIODT", "เพียเรียด", "จบ ไม่มีอะไรต้องเถียงต่อ"),
      p("NO CAP ปังของจริง", "โน แคป", "เรื่องจริง ไม่โม้"),
      p("SHEESH!", "ชี้ช", "โอ้โห"),
      p("MAIN CHARACTER", "เมน คาแรกเตอร์", "คนเด่นเหมือนพระเอกนางเอก"),
      p("SLAY ยกชุด", "สเลย์", "ปังมาก"),
      p("CLEAN SWEEP W", "คลีน สวีป ดับเบิลยู", "เก็บเรียบทุกข้อ"),
    ],
  },
  {
    min: 90,
    label: "ปังมาก",
    pool: [
      p("SLAY", "สเลย์", "ปังมาก"),
      p("ATE", "เอท", "ทำได้ปังมาก"),
      p("FIRE", "ไฟเออร์", "เจ๋งมาก"),
      p("BUSSIN", "บัสซิน", "ดีมาก"),
      p("GOAT ENERGY", "โกท เอเนอร์จี้", "ฟีลสุดยอด"),
      p("GLOW UP", "โกลว์ อัพ", "ดูดีขึ้นมาก"),
      p("SIGMA", "ซิกม่า", "เก่ง มั่นใจ ไม่ตามใคร"),
      p("BIG W", "บิ๊ก ดับเบิลยู", "ชนะครั้งใหญ่"),
      p("LIT", "ลิท", "เจ๋ง สนุกมาก"),
      p("HIGHKEY เก่ง", "ไฮคีย์", "เก่งแบบชัดเจนมาก"),
    ],
  },
  {
    min: 80,
    label: "เก่งจริง",
    pool: [
      p("W", "ดับเบิลยู", "ชนะ"),
      p("VIBING", "ไวบิง", "กำลังลื่นไหล"),
      p("SAME ENERGY", "เซม เอเนอร์จี้", "ฟีลเดียวกันเลย"),
      p("FOR REAL", "ฟอร์ เรียล", "จริงจริง ไม่ได้โม้"),
      p("NO CAP", "โน แคป", "เรื่องจริง ไม่โกหก"),
      p("GLOW UP กำลังมา", "โกลว์ อัพ", "กำลังดีขึ้นเรื่อย ๆ"),
      p("SOLID W", "โซลิด ดับเบิลยู", "ชนะแบบแน่น ๆ"),
      p("BET", "เบท", "เอาเลย ได้เลย"),
      p("LOWKEY ปัง", "โลว์คีย์", "แอบปังนะเนี่ย"),
    ],
  },
  {
    min: 70,
    label: "ผ่านสบาย",
    pool: [
      p("W นะเนี่ย", "ดับเบิลยู", "ชนะนะ"),
      p("LOWKEY เก่ง", "โลว์คีย์", "แอบเก่งนิดหน่อย"),
      p("VIBE ดี", "ไวบ์", "บรรยากาศดี"),
      p("BET", "เบท", "ได้เลย"),
      p("ALMOST SLAY", "ออลโมสต์ สเลย์", "เกือบปังแล้ว"),
      p("GLOW UP โหลดอยู่", "โกลว์ อัพ", "กำลังจะดีขึ้น"),
      p("NOT MID", "น็อท มิด", "ไม่ธรรมดานะ"),
      p("RESPECT W", "ริสเป็ค ดับเบิลยู", "ชนะแบบน่านับถือ"),
    ],
  },
  {
    min: 60,
    label: "ผ่านแบบเฉียด",
    pool: [
      p("MID+", "มิด พลัส", "ธรรมดา ๆ แต่ดีกว่ากลาง"),
      p("LOWKEY ผ่าน", "โลว์คีย์", "ผ่านแบบเงียบ ๆ"),
      p("VIBING แต่ยังไม่สุด", "ไวบิง", "ไปได้ แต่ยังไม่สุด"),
      p("BRUH เฉียดไปนิด", "บราห์", "เอ้ย เกือบแล้ว"),
      p("GLOW UP รอบหน้า", "โกลว์ อัพ", "รอบหน้าดีขึ้นแน่"),
      p("HALF W", "ฮาล์ฟ ดับเบิลยู", "ชนะครึ่งเดียว"),
      p("SUS นิด ๆ", "ซัส", "น่าสงสัยนิดหน่อย"),
    ],
  },
  {
    min: 50,
    label: "ยังไม่ผ่าน แต่ใกล้แล้ว",
    pool: [
      p("MID", "มิด", "ธรรมดา ไม่ว้าว"),
      p("BRUH", "บราห์", "เอ้ย"),
      p("ยังไม่ COOKED", "คุกต์", "ยังไม่จบ ยังแก้ได้"),
      p("GLOW UP LOADING", "โกลว์ อัพ โหลดดิง", "กำลังโหลดความเก่ง"),
      p("SKIBIDI นิดนึง", "สกิบิดิ", "คำมั่วไร้สาระแบบตลก"),
      p("ALMOST BET", "ออลโมสต์ เบท", "เกือบได้แล้ว"),
      p("OHIO นิดหน่อย", "โอไฮโอ", "แปลก ๆ หลุด ๆ นิดหน่อย"),
    ],
  },
  {
    min: 0,
    label: "ต้องซ้อมอีกหน่อย",
    pool: [
      p("BIG L แต่ยังไม่ COOKED", "บิ๊ก แอล", "พลาดหนัก แต่ยังไม่จบ"),
      p("LET ME COOK", "เล็ต มี คุก", "ขอเวลาโชว์ฝีมือหน่อย"),
      p("GLOW UP ยังไม่เริ่ม", "โกลว์ อัพ", "ความเก่งยังไม่เริ่มโหลด"),
      p("WARM UP รอบเดียว", "วอร์ม อัพ", "นี่แค่อุ่นเครื่อง"),
      p("TOUCH GRASS แล้วกลับมาใหม่", "ทัช กราส", "ไปพักแล้วค่อยกลับมาลุย"),
      p("BRUH MOMENT", "บราห์ โมเมนต์", "ช่วงเวลาเอ้ย"),
      p("RUN IT BACK", "รัน อิท แบ็ค", "เอาใหม่อีกรอบ"),
      p("COMEBACK ARC", "คัมแบ็ค อาร์ค", "ช่วงกลับมาทวงคืน"),
    ],
  },
];

/** FNV-1a 32 บิต — เล็ก เร็ว และให้ผลเดิมทุกเครื่องทุกเบราว์เซอร์ (ไม่พึ่ง Math.random) */
function hash(text: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < text.length; i += 1) {
    h ^= text.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h >>> 0;
}

export function bandOf(pct: number): Band {
  const clamped = Number.isFinite(pct) ? Math.min(100, Math.max(0, pct)) : 0;
  return BANDS.find((b) => clamped >= b.min) ?? BANDS[BANDS.length - 1]!;
}

/**
 * เลือกคำชมของ attempt หนึ่ง ๆ — **ผลเดิมเสมอสำหรับ attempt เดิม**
 *
 * @param pct เปอร์เซ็นต์ที่ปัดแล้ว (0-100)
 * @param attemptId id ของ attempt ใช้เป็นเมล็ดสุ่ม
 */
export function pickPraise(pct: number, attemptId: string): Praise {
  const band = bandOf(pct);
  return band.pool[hash(attemptId) % band.pool.length]!;
}
